import React from 'react';
import { 
  FaWhatsapp, 
  FaEnvelope,   
  FaQuoteLeft,
  FaStar,
  FaPhoneAlt, 
  FaClock,
  FaCalendarAlt,
  FaArrowRight,
  FaShieldAlt
} from 'react-icons/fa';
import { motion } from 'framer-motion';

const Contact = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#000324] via-[#010536] to-[#020a5c]">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(253,170,1,0.1)_0%,transparent_50%),radial-gradient(circle_at_80%_20%,rgba(99,102,241,0.1)_0%,transparent_50%)]" />
        <div className="absolute top-20 left-10 w-64 h-64 bg-[#fdaa01]/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-float [animation-delay:5s]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center text-xs font-semibold uppercase tracking-wider text-[#fdaa01] mb-4 px-4 py-2 bg-[#fdaa01]/10 rounded-full border border-[#fdaa01]/20">
              <FaEnvelope className="mr-2" />
              Get in Touch
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6">
              Let's Build Something{' '}
              <span className="bg-gradient-to-r from-[#fdaa01] to-[#ffc042] bg-clip-text text-transparent">
                Great Together
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">
              Ready to transform your business with custom digital solutions? 
              Reach out today for a free consultation.
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-[#fdaa01] to-transparent mx-auto mt-8" />
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Left Column - Contact Options */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              {/* WhatsApp Card */}
              <motion.a
                href="https://wa.me/254762272152"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-green-500 rounded-3xl p-8 block shadow-xl hover:shadow-2xl transition-all duration-300 relative overflow-hidden"
                whileHover={{ scale: 1.02, y: -5 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
                
                <div className="relative z-10 flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                      <FaWhatsapp className="text-3xl text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">WhatsApp Quick Chat</h3>
                      <p className="text-white/90 mb-4">Get instant responses and quick estimates</p>
                      <span className="inline-flex items-center text-white font-semibold group-hover:translate-x-1 transition-transform duration-300">
                        Start Chat
                        <FaArrowRight className="ml-2" />
                      </span>
                    </div>
                  </div>
                  <span className="flex items-center gap-2 text-xs font-semibold text-white bg-white/20 px-3 py-1.5 rounded-full">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    Online Now
                  </span>
                </div>
              </motion.a>

              {/* Email Card */}
              <motion.a
                href="mailto:shyeecodes@gmail.com"
                className="group bg-white rounded-3xl p-8 block shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 relative overflow-hidden"
                whileHover={{ scale: 1.02, y: -5 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#fdaa01]/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-500" />
                
                <div className="relative z-10 flex items-start gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-[#010536] flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                    <FaEnvelope className="text-2xl text-[#fdaa01]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#010536] mb-2">Send Detailed Inquiry</h3>
                    <p className="text-gray-600 mb-4">For comprehensive project discussions</p>
                    <span className="inline-flex items-center text-[#fdaa01] font-semibold group-hover:translate-x-1 transition-transform duration-300">
                      Open Email
                      <FaArrowRight className="ml-2" />
                    </span>
                  </div>
                </div>
              </motion.a>

              {/* Contact Info Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <a 
                  href="tel:+254762272152"
                  className="group bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300"
                >
                  <FaPhoneAlt className="text-2xl text-[#fdaa01] mb-3" />
                  <h4 className="text-white font-bold mb-1">Call Us</h4>
                  <span className="text-white/70 group-hover:text-white transition-colors duration-300">
                    +254 762 272 152
                  </span>
                </a>
                
                <div className="group bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300">
                  <FaClock className="text-2xl text-[#fdaa01] mb-3" />
                  <h4 className="text-white font-bold mb-1">Response Time</h4>
                  <p className="text-white/70">Within 1-2 hours during business hours</p>
                </div>
                
                <a 
                  href="https://calendar.app.google/Ti8ismLSofY2gGci8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 sm:col-span-2"
                >
                  <FaCalendarAlt className="text-2xl text-[#fdaa01] mb-3" />
                  <h4 className="text-white font-bold mb-1">Schedule a Call</h4>
                  <p className="text-white/70">Book a free 30-minute consultation</p>
                </a>
              </div>
            </motion.div>

            {/* Right Column - Testimonial & Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="space-y-6"
            >
              {/* Testimonial Card */}
              <div className="bg-white rounded-3xl p-8 shadow-xl">
                <div className="w-12 h-12 rounded-2xl bg-[#fdaa01]/10 flex items-center justify-center mb-6">
                  <FaQuoteLeft className="text-2xl text-[#fdaa01]" />
                </div>
                <blockquote className="text-gray-700 text-lg leading-relaxed mb-6">
                  "Give your energy to the right environment, and good things will grow. 
                  Sheila helped us build a digital system that tripled our lead generation 
                  in just 3 months."
                </blockquote>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <div className="font-bold text-[#010536]">Mike Mwangi</div>
                    <div className="text-sm text-gray-500">CEO, Tsavo Cats Safaris</div>
                  </div>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} className="text-[#fdaa01]" />
                    ))}
                  </div>
                </div>
              </div>

              {/* Process Info */}
              <div className="bg-gradient-to-br from-[#010536] to-[#020a5c] rounded-3xl p-8 text-white border border-[#fdaa01]/10">
                <h4 className="text-xl font-bold mb-6 flex items-center gap-3">
                  <FaShieldAlt className="text-[#fdaa01]" />
                  Our Process
                </h4>
                <div className="space-y-4">
                  {[
                    'Initial Consultation',
                    'Strategy Development',
                    'Proposal & Timeline',
                    'Project Kickoff'
                  ].map((step, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <span className="w-8 h-8 rounded-full bg-[#fdaa01] text-[#010536] font-bold flex items-center justify-center flex-shrink-0">
                        {index + 1}
                      </span>
                      <span className="text-white/80">{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;